import { useMemo, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Icon } from "@iconify/react";
import Page from "../Page";
import { BackButton } from "src/common/components/BackButton";
import { Loader } from "../Loader";
import { RootTabItemStyles } from "src/theme/overrides/Tabs";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useGetDetail, useList } from "src/hooks/useFetch.hook";
import { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import { toList, statusChipColor } from "./fleet.constants";
import PortalAccessSection from "./PortalAccessSection";
import DriversTab from "./tabs/DriversTab";
import VehiclesTab from "./tabs/VehiclesTab";
import ChargerAccessTab from "./tabs/ChargerAccessTab";
import TariffsTab from "./tabs/TariffsTab";
import WalletTab from "./tabs/WalletTab";
import SessionsTab from "./tabs/SessionsTab";

const RootTabStyle = styled(TabList)(({ theme, length }) => ({
  width: "98%",
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
  "& .MuiTabs-flexContainer": {
    justifyContent: "left",
  },
  "& .MuiTab-root": {
    width: `${100 / length}%`,
  },
}));

const fleetOperatorTabList = [
  { value: "overview", label: "Overview" },
  { value: "drivers", label: "Drivers" },
  { value: "vehicles", label: "Vehicles" },
  { value: "charger-access", label: "Charger Access" },
  { value: "tariffs", label: "Tariffs" },
  { value: "wallet", label: "Wallet" },
  { value: "sessions", label: "Sessions & Reports" },
];

const OverviewField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value || "-"}</Typography>
  </Grid>
);

const QuickStat = ({ label, value }) => (
  <Card sx={{ p: 2, textAlign: "center" }} variant="outlined">
    <Typography variant="h5">{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Card>
);

const FleetOperatorDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(
    fleetOperatorTabList.some(({ value }) => value === searchParams.get("tab"))
      ? searchParams.get("tab")
      : "overview"
  );

  const { data: operator, isFetching } = useGetDetail("fleetOperator", { id });
  const { data: driversData } = useList("fleetDrivers", undefined, id);
  const { data: vehiclesData } = useList("fleetVehicles", undefined, id);

  const drivers = useMemo(() => toList(driversData), [driversData]);
  const vehicles = useMemo(() => toList(vehiclesData), [vehiclesData]);

  const isFleetEnabled =
    user?.company?.enabledFeatures?.includes(FEATURE.FLEET_MANAGEMENT) ?? false;

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  if (!isFleetEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isFetching) {
    return <Loader />;
  }

  return (
    <Page title="Fleet Operator | Smart-CMS">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <BackButton text={operator?.name ?? "Fleet Operator"} path="/fleet" />
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            size="small"
            label={operator?.status ?? "-"}
            color={statusChipColor(operator?.status)}
          />
          {hasUpdateAccess && !isScopedOperator && (
            <Button
              variant="contained"
              startIcon={<Icon icon="eva:edit-outline" />}
              onClick={() => navigate(`/fleet/operators/${id}/edit`)}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Stack>
      <TabContext value={selectedTab}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={fleetOperatorTabList.length}
            onChange={handleTabChange}
          >
            {fleetOperatorTabList.map((tab) => (
              <RootTabItemStyles
                key={tab.value}
                value={tab.value}
                label={tab.label}
              />
            ))}
          </RootTabStyle>
        </Stack>
        <Card sx={{ p: 3 }}>
          <TabPanel value="overview">
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <QuickStat label="Drivers" value={drivers.length} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <QuickStat label="Vehicles" value={vehicles.length} />
              </Grid>
            </Grid>
            <Typography variant="h6" gutterBottom>
              Operator Details
            </Typography>
            <Grid container spacing={2}>
              <OverviewField label="Name" value={operator?.name} />
              <OverviewField label="Legal Name" value={operator?.legal_name} />
              <OverviewField label="Fleet Type" value={operator?.fleet_type} />
              <OverviewField
                label="Contact Name"
                value={operator?.contact_name}
              />
              <OverviewField
                label="Contact Email"
                value={operator?.contact_email}
              />
              <OverviewField
                label="Contact Phone"
                value={operator?.contact_phone}
              />
              <OverviewField
                label="Billing Email"
                value={operator?.billing_email}
              />
              <OverviewField
                label="External Reference"
                value={operator?.external_ref}
              />
              <OverviewField label="Status" value={operator?.status} />
              <OverviewField
                label="Created At"
                value={fDateTime(operator?.created_at)}
              />
              <OverviewField
                label="Updated At"
                value={fDateTime(operator?.updated_at)}
              />
            </Grid>
            <PortalAccessSection operatorId={id} />
          </TabPanel>
          <TabPanel value="drivers">
            <DriversTab operatorId={id} />
          </TabPanel>
          <TabPanel value="vehicles">
            <VehiclesTab operatorId={id} />
          </TabPanel>
          <TabPanel value="charger-access">
            <ChargerAccessTab operatorId={id} />
          </TabPanel>
          <TabPanel value="tariffs">
            <TariffsTab operatorId={id} />
          </TabPanel>
          <TabPanel value="wallet">
            <WalletTab operatorId={id} />
          </TabPanel>
          <TabPanel value="sessions">
            <SessionsTab operatorId={id} />
          </TabPanel>
        </Card>
      </TabContext>
    </Page>
  );
};

export default FleetOperatorDetail;
